import React from 'react';
import { objectToQuery } from 'helpers/Formatter';
import { apiGet } from 'helpers/ApiHelper';
import Auth from 'j-toker';
import { addMinutesToTimeStr, generateOpenCalendarSlots } from 'helpers/DateHelper';
import DatePicker from 'components/ui/DatePicker';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import Spacer from 'components/ui/Spacer';
import CtaText from 'components/ui/CtaCard';
import { Form as AntdForm } from 'antd';
import moment from 'moment';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { deepDiver } from 'helpers/utilities';

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const StyledColumn = styled(Col)`
  ${(props) =>
    props.hideTitle &&
    css`
      margin: 0;
    `};
`;

const StyledRow = styled(Row)`
  ${(props) =>
    props.hideTitle &&
    css`
      width: 300px;

      @media (max-width: 400px) {
        width: 100%;
        margin: auto;
      }
    `};
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const FormHeading = styled.div`
  font-family: 'Circular-Bold';
`;

const StyledCtaText = styled(CtaText)`
  padding: 0px;
`;

const StyledSpacer = styled(Spacer)`
  ${(props) =>
    props.maxHeight &&
    css`
       {
        max-height: 100%;
      }
    `};
`;

const TimeSlot = styled.div`
  border-radius: 8px;
  border: solid 1px #d9d9e3;
  background-color: #ffffff;
  cursor: pointer;
  padding: 8px 8px 6px;
  margin: 12px 8px;
  text-align: center;

  &:hover {
    background-color: var(--light-blue-grey);
    color: #230cc2;
  }

  ${(props) =>
    props.selected &&
    css`
      background-color: var(--light-blue-grey);
      color: #230cc2;
      border-color: var(--ultramarine-blue);
      border-width: 2px;
      margin: 11px 7px;
    `};
`;

class CreateShowingDrawerSearchStep extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingSlots: false,
      formComplete: false,
      searchForm: {},
      availableTimes: [],
      nextAvailableDate: null,
      unitTypes: [],
      selectedTime: null,
      showForceBook: false,
      selectedAgent: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.unitTypes !== this.props.unitTypes) {
      this.setState({
        unitTypes: this.props.unitTypes,
      });

      this.setSearchField('propertyId', this.props.property.id);
    }
  }

  handleFormCompleted = () => {
    this.setState({
      selectedTime: null,
      formComplete: true,
      loadingSlots: true,
      showForceBook: false,
    });
    this.props.setSlotSearchData(null);

    const { property } = this.props;
    const unitTypeIds = property?.hide_unit_types ? 'all' : this.state.searchForm.unitType;

    const queryObj = {
      property_id: this.state.searchForm.propertyId,
      requested_date: this.state.searchForm.requestedDate,
      unit_type_ids: unitTypeIds,
      is_prospect: this.props.isProspect,
    };

    // # We need to add 1 to the day of week to align with our backend
    let dayOfWeek = new Date(queryObj.requested_date).getDay() + 1;
    if (dayOfWeek === 7) {
      // # We need to set Sunday to 0 to match our backend logic
      dayOfWeek = 0;
    }

    if (!this.props.isProspect) {
      this.props.getAgents(dayOfWeek);
    }

    apiGet(
      `${Auth.getApiUrl()}/bookings/apartment_showings/find_availability${objectToQuery(queryObj)}`
    )
      .done((response) => {
        const showingLength = response.availabilities.showing_length;

        if (response.availabilities.lucky) {
          const allSlots = response.availabilities.availabilities.day_0.available_times;
          let availableTimes = [];
          allSlots.forEach((slot) => {
            if (slot) {
              availableTimes.push([slot, addMinutesToTimeStr(slot, showingLength)]);
            }
          });
          this.setState({
            showingLength,
            availableTimes: availableTimes,
            nextAvailableDate: null,
            loadingSlots: false,
          });
        } else {
          this.setState({
            showingLength,
            availableTimes: [],
            nextAvailableDate: response.availabilities.availabilities.day_0.date,
            loadingSlots: false,
          });
        }
      })
      .catch((error) => {
        this.setState({
          loadingSlots: false,
          availableTimes: [],
        });
      });
  };

  showForceBookForm = () => {
    const { showForceBook, showingLength } = this.state;

    if (showForceBook) {
      this.setSearchField('agentId', null);
    }

    let availableTimes = [];
    if (!showForceBook) {
      generateOpenCalendarSlots(showingLength).forEach((slot) =>
        availableTimes.push([slot, addMinutesToTimeStr(slot, showingLength)])
      );
    }
    this.setState((prevState) => ({
      availableTimes,
      showForceBook: !prevState.showForceBook,
    }));
  };

  handlePropertyChange = (value) => {
    apiGet(`${Auth.getApiUrl()}/landlords/unit_types_available_for_showings/${value}`)
      .done((response) => {
        this.setState({
          unitTypes: response.unit_types,
        });
        this.setSearchField('unitType', []);
      })
      .catch((error) => {});

    this.setSearchField('propertyId', value);
    this.props.getLandlordTrafficSources(value);
  };

  handleAgentChange = (value) => {
    this.setSearchField('agentId', value);
  };

  setSearchField = (key, value) => {
    this.setState(
      {
        searchForm: {
          ...this.state.searchForm,
          [key]: value,
        },
      },
      () => {
        const { propertyId, unitType, requestedDate, agentId } = this.state.searchForm;
        const { property } = this.props;

        const unitTypeDefined = property?.hide_unit_types || typeof unitType !== 'undefined';

        if (
          (agentId === null || typeof agentId === 'undefined') &&
          typeof propertyId !== 'undefined' &&
          unitTypeDefined &&
          requestedDate
        ) {
          this.handleFormCompleted();
        }
      }
    );
  };

  handleSearchNextAvailableDate = () => {
    this.setState(
      {
        searchForm: {
          ...this.state.searchForm,
          requestedDate: moment(new Date(Date.parse(this.state.nextAvailableDate))),
        },
      },
      () => {
        this.handleFormCompleted();
      }
    );
  };

  handleTimeSlotSelect = (timeSlot) => {
    const { searchForm } = this.state;

    let property = this.props.property;
    if (!property) {
      property = this.props.properties.find((property) => property.value === searchForm.propertyId);
    }

    this.props.setSlotSearchData({
      property,
      unitType: property.hide_unit_types
        ? this.state.unitTypes
        : this.state.unitTypes.filter((unitType) => searchForm.unitType.includes(unitType.value)),
      bookDate: searchForm.requestedDate,
      agentId: searchForm.agentId,
      timeSlot,
    });
    this.setState({ selectedTime: timeSlot[0] });
  };

  render() {
    return (
      <React.Fragment>
        {!this.props.hideTitle && (
          <Spacer mb={1}>
            <FormTitle>{getTranslatedString('showingDrawer.showingDetails')}</FormTitle>
          </Spacer>
        )}
        <AntdForm layout="vertical">
          <Spacer mb={1}>
            <StyledRow hideTitle={this.props.hideTitle} gutter={16}>
              <StyledColumn hideTitle={this.props.hideTitle} span={12} xs={24} sm={12}>
                {this.renderPropertyField()}
              </StyledColumn>
              <StyledColumn
                hideTitle={this.props.hideTitle}
                span={12}
                xs={24}
                sm={this.props.property ? 24 : 12}
              >
                {this.renderUnitTypeField()}
              </StyledColumn>
            </StyledRow>
          </Spacer>
          <Spacer mb={2}>
            <StyledRow hideTitle={this.props.hideTitle} gutter={16}>
              <StyledColumn
                hideTitle={this.props.hideTitle}
                span={12}
                xs={24}
                sm={this.props.property ? 24 : 12}
              >
                {this.renderBookDateField()}
              </StyledColumn>
            </StyledRow>
          </Spacer>
          <StyledSpacer maxHeight={this.props.maxHeight} mb={1}>
            {this.renderTimeSlotsSection()}
          </StyledSpacer>
        </AntdForm>
      </React.Fragment>
    );
  }

  renderPropertyField = () => {
    const { properties, property } = this.props;
    const { propertyId } = this.state.searchForm;

    if (!!property) return;

    return (
      <Form.Item label={getTranslatedString('showingDrawer.selectAProperty')}>
        <FullWidthSelect
          showSearch
          placeholder={getTranslatedString('showingDrawer.select')}
          size="large"
          value={propertyId}
          onChange={this.handlePropertyChange}
          filterOption={(input, option) => {
            const desiredPropertyOption = deepDiver(option, 'props.children');
            if (!desiredPropertyOption) return;

            return desiredPropertyOption.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
        >
          {properties.map((property) => (
            <Select.Option value={property.value} key={property.value}>
              {property.label}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderUnitTypeField = () => {
    const { unitTypes } = this.state;
    const { unitType } = this.state.searchForm;
    const { property } = this.props;

    if (property && property.hide_unit_types) return;
    return (
      <Form.Item label={getTranslatedString('showingDrawer.selectAUnitType')}>
        <FullWidthSelect
          mode="multiple"
          showSearch
          placeholder={getTranslatedString('showingDrawer.select')}
          size="large"
          value={unitType}
          onChange={(value) => this.setSearchField('unitType', value)}
          filterOption={(input, option) => {
            const desiredUnitOption = deepDiver(option, 'props.children.1');
            if (!desiredUnitOption) return;

            return desiredUnitOption.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          disabled={unitTypes.length === 0}
        >
          {unitTypes.map((unitType) =>
            unitType && Number.isInteger(unitType.value) ? (
              <Select.Option value={unitType.value}>
                {!unitType.translation_key && unitType.label}
                {unitType.translation_key &&
                  getTranslatedString(`standardUnitTypes.${unitType.translation_key}`)}
              </Select.Option>
            ) : null
          )}
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderBookDateField = () => {
    const { requestedDate } = this.state.searchForm;
    const yesterday = moment().subtract(1, 'day').endOf('day');
    const thirtyDaysFromNow = moment().add(30, 'day').endOf('day');

    return (
      <Form.Item label={getTranslatedString('showingDrawer.bookingDate')}>
        <FullWidthDatePicker
          hideTitle={this.props.hideTitle}
          size="large"
          value={requestedDate}
          placeholder={getTranslatedString('showingDrawer.selectADate')}
          defaultPickerValue={moment().locale(getCurrentLocale())}
          showToday={false}
          minDate={new Date()}
          mobileMode="date"
          maxDate={thirtyDaysFromNow}
          disabledDate={(current) =>
            current && !(yesterday.isSameOrBefore(current) && thirtyDaysFromNow.isAfter(current))
          }
          onChange={(value) => this.setSearchField('requestedDate', value)}
        />
      </Form.Item>
    );
  };

  renderTimeSlotsSection = () => {
    const { loadingSlots, formComplete, nextAvailableDate, showForceBook } = this.state;
    const { agentId } = this.state.searchForm;

    return (
      <React.Fragment>
        <Spacer mb={0.5}>
          <FormHeading>{getTranslatedString('showingDrawer.selectATimeSlot')}</FormHeading>
          {!formComplete && <div>{getTranslatedString('showingDrawer.completeTheForm')}</div>}
          {formComplete && !loadingSlots && (
            <div>{getTranslatedString('showingDrawer.changeToSearchAgain')}</div>
          )}
          {this.props.showForceBooking && formComplete && !loadingSlots && (
            <Spacer mb={1}>
              <button type="button" className="link-button" onClick={this.showForceBookForm}>
                {showForceBook
                  ? getTranslatedString('showingDrawer.viewAvailabilities')
                  : getTranslatedString('showingDrawer.clickToManualBook')}
              </button>
            </Spacer>
          )}

          {loadingSlots && <div>{getTranslatedString('showingDrawer.loadingAvailableTimes')}</div>}
        </Spacer>
        <Row gutter={16}>
          {showForceBook && this.renderForceBookForm(this.props.agents)}
          {loadingSlots && this.renderSkeletonSlots()}
          {((formComplete && !loadingSlots && !showForceBook) || (showForceBook && agentId)) &&
            this.renderTimeSlots()}
          {formComplete &&
            !showForceBook &&
            !loadingSlots &&
            nextAvailableDate &&
            this.renderNextAvailableDate()}
        </Row>
      </React.Fragment>
    );
  };

  renderForceBookForm = (agents) => {
    const { agentId } = this.state.searchForm;

    return (
      <Col span={24}>
        <Spacer mb={1}>
          <FormTitle>{getTranslatedString('showingDrawer.manualBook')}</FormTitle>
          <p>{getTranslatedString('showingDrawer.manualBookDescription')}</p>
        </Spacer>
        <Spacer mb={1}>
          <Form.Item label={getTranslatedString('showingDrawer.selectAnAgent')}>
            <FullWidthSelect
              showSearch
              value={agentId}
              placeholder={getTranslatedString('showingDrawer.select')}
              size="large"
              onChange={this.handleAgentChange}
              filterOption={(input, option) => {
                const desiredAgentOption = deepDiver(option, 'props.children');
                if (!desiredAgentOption) return;

                return desiredAgentOption.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            >
              {agents.map((agent) => (
                <Select.Option value={agent.value} key={agent.value}>
                  {agent.label}
                </Select.Option>
              ))}
            </FullWidthSelect>
          </Form.Item>
        </Spacer>
      </Col>
    );
  };

  renderSkeletonSlots = () => {
    return (
      <React.Fragment>
        {Array(3)
          .fill(0)
          .map((_i) => {
            return (
              <Col span="8">
                <Skeleton height={32} duration={1} />
              </Col>
            );
          })}
      </React.Fragment>
    );
  };

  renderTimeSlots = () => {
    const { selectedTime } = this.state;
    return this.state.availableTimes.map((item) => (
      <Col xs={24} sm={12} md={8}>
        <TimeSlot
          selected={item[0] === selectedTime}
          onClick={() => this.handleTimeSlotSelect(item)}
        >
          {item[0]} - {item[1]}
        </TimeSlot>
      </Col>
    ));
  };

  renderNextAvailableDate = () => {
    return (
      <Col span="24">
        <Spacer mb={0.5}>
          <div>{getTranslatedString('showingDrawer.noAvailabilitiesFound')}.</div>
        </Spacer>
        <StyledCtaText onClick={this.handleSearchNextAvailableDate}>
          {getTranslatedString('showingDrawer.clickToSeeAvailabilitiesFor')}{' '}
          {this.state.nextAvailableDate}
        </StyledCtaText>
        .
      </Col>
    );
  };
}

export default CreateShowingDrawerSearchStep;
